import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const QRScannerModal = ({ isOpen, onClose, onScanSuccess, onManualEntry }) => {
  const [manualTicketId, setManualTicketId] = useState('');
  const [scanMode, setScanMode] = useState('camera'); // 'camera' or 'manual'
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setManualTicketId('');
      setScanResult(null);
      setIsScanning(false);
    }
  }, [isOpen]);

  const handleStartScan = () => {
    setIsScanning(true);
    // Simulate QR code scanning with mock data
    setTimeout(() => {
      const mockTicketId = `TKT-${Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase()}`;
      setScanResult(mockTicketId);
      setIsScanning(false);
      onScanSuccess(mockTicketId);
    }, 2000);
  };

  const handleManualSubmit = (e) => {
    e?.preventDefault();
    if (manualTicketId?.trim()) {
      onManualEntry(manualTicketId?.trim());
      setManualTicketId('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="QrCode" size={24} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Check-in Scanner</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Mode Selector */}
          <div className="flex items-center space-x-2 bg-muted rounded-lg p-1">
            <button
              onClick={() => setScanMode('camera')}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                scanMode === 'camera' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Camera" size={16} />
              <span>Camera Scan</span>
            </button>
            <button
              onClick={() => setScanMode('manual')}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                scanMode === 'manual' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Keyboard" size={16} />
              <span>Manual Entry</span>
            </button>
          </div>

          {/* Camera Scan Mode */}
          {scanMode === 'camera' && (
            <div className="space-y-4">
              <div className="aspect-square bg-muted rounded-lg flex items-center justify-center border-2 border-dashed border-border">
                {isScanning ? (
                  <div className="text-center space-y-4">
                    <div className="animate-spin">
                      <Icon name="QrCode" size={48} className="text-primary" />
                    </div>
                    <p className="text-sm text-muted-foreground">Scanning for QR code...</p>
                  </div>
                ) : scanResult ? (
                  <div className="text-center space-y-4">
                    <Icon name="CheckCircle" size={48} className="text-success" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Scan Successful!</p>
                      <p className="text-xs text-muted-foreground">Ticket ID: {scanResult}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center space-y-4">
                    <Icon name="QrCode" size={48} className="text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Position QR code within the frame
                    </p>
                  </div>
                )}
              </div>

              <Button
                variant="primary"
                onClick={handleStartScan}
                disabled={isScanning}
                loading={isScanning}
                iconName="Camera"
                iconPosition="left"
                className="w-full"
              >
                {isScanning ? 'Scanning...' : 'Start Camera Scan'}
              </Button>
            </div>
          )}

          {/* Manual Entry Mode */}
          {scanMode === 'manual' && (
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <Input
                label="Ticket ID"
                type="text"
                placeholder="Enter ticket ID (e.g., TKT-ABC123)"
                value={manualTicketId}
                onChange={(e) => setManualTicketId(e?.target?.value)}
                description="Enter the ticket ID manually for check-in"
                required
              />
              <Button
                type="submit"
                variant="primary"
                disabled={!manualTicketId?.trim()}
                iconName="UserCheck"
                iconPosition="left"
                className="w-full"
              >
                Check In Attendee
              </Button>
            </form>
          )}

          {/* Instructions */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={16} className="text-primary mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Quick Tips:</p>
                <ul className="space-y-1 text-xs">
                  <li>• Use camera scan for fastest check-in</li>
                  <li>• Manual entry works for damaged QR codes</li>
                  <li>• Ticket IDs are case-insensitive</li>
                  <li>• Already checked-in attendees will show a warning</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QRScannerModal;